import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { CohortStatus, CohortSummary } from '../api/types';

/**
 * Cohort query hooks — backed by `services/course/handler.py`
 * (operationIds: listCohorts, createCohort, getCohort).
 */

export const cohortKeys = {
  all: ['cohorts'] as const,
  list: () => [...cohortKeys.all, 'list'] as const,
  detail: (id: string) => [...cohortKeys.all, 'detail', id] as const,
};

export function useCohorts() {
  return useQuery<ReadonlyArray<CohortSummary>>({
    queryKey: cohortKeys.list(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/cohorts', { method: 'GET', signal });
      if (Array.isArray(resp)) return resp as ReadonlyArray<CohortSummary>;
      const items = (resp as { items?: ReadonlyArray<CohortSummary> })?.items;
      return items ?? [];
    },
    staleTime: 60_000,
  });
}

export interface CohortCourse {
  id: number;
  slug: string;
  name: string;
  enrolled: number;
  member_count: number;
}

/**
 * Courses assigned to a cohort — i.e. courses its members are enrolled in, with
 * how many of the cohort are in each. Backed by GET /cohorts/{id}/courses.
 */
export function useCohortCourses(id: string | undefined) {
  return useQuery<{ items: ReadonlyArray<CohortCourse>; member_count: number }>({
    queryKey: [...cohortKeys.detail(id ?? ''), 'courses'],
    enabled: Boolean(id),
    staleTime: 15_000,
    queryFn: async ({ signal }) =>
      apiFetch<{ items: ReadonlyArray<CohortCourse>; member_count: number }>('/cohorts/{id}/courses', {
        method: 'GET',
        pathParams: { id: id ?? '' },
        signal,
      }),
  });
}

export function useCohort(id: string | undefined) {
  return useQuery<CohortSummary>({
    queryKey: cohortKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async ({ signal }) =>
      apiFetch<CohortSummary>('/cohorts/{id}', {
        method: 'GET',
        pathParams: { id: id ?? '' },
        signal,
      }),
    staleTime: 60_000,
  });
}

export interface CreateCohortInput {
  name: string;
  courseId: string;
  status?: CohortStatus;
}

export function useCreateCohort() {
  const qc = useQueryClient();
  return useMutation<CohortSummary, Error, CreateCohortInput>({
    mutationFn: async (input) =>
      apiFetch<CohortSummary>('/cohorts', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cohortKeys.list() });
    },
  });
}


export function useAddCohortMembers(id: string) {
  const qc = useQueryClient();
  return useMutation<{ added: number; unknown: string[]; skipped?: string[] }, Error, { usernames?: string[]; user_ids?: number[] }>({
    mutationFn: async (input) =>
      apiFetch<{ added: number; unknown: string[]; skipped?: string[] }>('/cohorts/{id}/members', {
        method: 'POST',
        pathParams: { id },
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cohortKeys.all });
      qc.invalidateQueries({ queryKey: ['gradebook'] });
    },
  });
}

export function useRemoveCohortMember(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { userId: number }>({
    mutationFn: async ({ userId }) =>
      apiFetch('/cohorts/{id}/members/{userId}', {
        method: 'DELETE',
        pathParams: { id, userId: String(userId) },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cohortKeys.all });
      qc.invalidateQueries({ queryKey: ['gradebook'] });
    },
  });
}

